import React, { useContext, useEffect, useRef, useState } from 'react';
import Chart, { ArcElement } from 'chart.js/auto';
import { useThemeContext } from 'src/context/ThemeProvider';
import Color from 'color';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ThemeContext } from 'src/context/ThemeProvider';
import { Box } from '@mui/material';
import { ScoreSalaryDistribution } from 'types';

interface Props {
	scoreChatValues: ScoreSalaryDistribution;
	salaryChatValues: ScoreSalaryDistribution;
}
const DistributionChartForSalaryScore = ({ scoreChatValues, salaryChatValues }: Props) => {
	const ThemeContextState = useContext(ThemeContext);
	const chartInstanceRef = useRef<Chart | null>(null);
	const radialSkillScoreChartRef = useRef<HTMLCanvasElement>(null);
	const { themeMode } = useThemeContext();
	const [value, setValue] = useState('score');

	const getGraphColor = (label: string) => {
		if (value === 'score') {
			return '#A99FF8';
		} else {
			return '#00B4AF99';
		}
	};

	const [chartLabels, setChartLabels] = useState<string[]>([]);
	const [chartValues, setChartValues] = useState<number[]>([]);

	useEffect(() => {
		if (value === 'score') {
			setChartLabels(Object.keys(scoreChatValues));
			setChartValues(Object.values(scoreChatValues));
		} else {
			setChartLabels(Object.keys(salaryChatValues));
			setChartValues(Object.values(salaryChatValues));
		}
	}, [scoreChatValues, salaryChatValues, value]);

	const generateChartColors = (context: any) => {
		const { chart } = context;
		const chartLabels = chart.data.labels || [];

		// Generate colors based on labels
		return chartLabels.map((label: string) => {
			const baseColor = Color(getGraphColor(label));
			return baseColor.toString(); // Apply a lighter shade for the gradient
		});
	};

	const handleChange = (event: React.SyntheticEvent, newValue: string) => {
		setValue(newValue);
	};

	useEffect(() => {
		if (radialSkillScoreChartRef.current) {
			// Destroy the previous chart instance if it exists
			if (chartInstanceRef.current) {
				chartInstanceRef.current.destroy();
			}

			// Create a new chart instance
			const ctx = radialSkillScoreChartRef.current.getContext('2d');

			if (ctx) {
				chartInstanceRef.current = new Chart(ctx, {
					type: 'bar',
					data: {
						labels: chartLabels,
						datasets: [
							{
								label: value === 'score' ? 'Students' : 'Students',
								data: chartValues,
								backgroundColor: generateChartColors,
								borderColor: themeMode === 'light' ? '#ffffff' : '#55555633',
								borderWidth: 0.6,
								maxBarThickness: 30,
							},
						],
					},
					options: {
						responsive: true,
						maintainAspectRatio: false,
						elements: {
							bar: {
								borderRadius: {
									topLeft: 4,
									topRight: 4,
									bottomLeft: 0,
									bottomRight: 0,
								},
								borderSkipped: 'bottom',
							},
						},
						scales: {
							x: {
								beginAtZero: true,
								title: {
									display: true,
									text: value === 'score' ? 'Score  →' : 'Salary  →',
									color: themeMode === 'light' ? '#555770' : '#d7d4d4',
									font: {
										size: 12,
										// family: 'Inter',
										lineHeight: '18px',
										weight: 'normal',
									},
								},
								ticks: {
									color: themeMode === 'light' ? '#1c1c1c66' : '#d7d4d4',
									font: {
										size: 12, // Reduced font size for x-axis ticks
										// family: 'Inter',
									},
								},
								grid: {
									display: false, // Remove x-axis grid lines
								},
							},
							y: {
								beginAtZero: true,
								title: {
									display: true,
									text: 'No. of students  →',
									color: themeMode === 'light' ? '#555770' : '#d7d4d4',
									font: {
										size: 12,
										// family: 'Inter',
										lineHeight: '18px',
										weight: 'normal',
									},
								},
								ticks: {
									color: themeMode === 'light' ? '#1c1c1c66' : '#d7d4d4',
									font: {
										size: 12, // Reduced font size for x-axis ticks
										// family: 'Inter',
									},
								},
								grid: {
									color: 'rgba(28, 28, 28, 0.05)',
								},
							},
						},
						color: themeMode === 'light' ? '#636363' : '#d7d4d4',
						plugins: {
							legend: {
								display: false, // Disable default legend
							},
						},
						layout: {
							padding: {
								top: 0,
								right: 0,
								bottom: 0,
								left: 0,
							},
						},
						animation: {
							onComplete: function (animation) {
								const chart = animation.chart;
								const ctx = chart.ctx;
								const centerX = chart.chartArea.left + chart.chartArea.width / 2;
								const centerY = chart.chartArea.top + chart.chartArea.height / 2;

								ctx.save();
								ctx.font = 'bold 0.6rem Arial';
								ctx.textAlign = 'center';
								ctx.textBaseline = 'middle';
								chart.data.labels?.forEach((label, index) => {
									const dataPoint = chart.getDatasetMeta(0).data[index] as ArcElement;
									const angle = dataPoint.startAngle + (dataPoint.endAngle - dataPoint.startAngle) / 2;

									const chartRadius =
										Math.min(
											chart.chartArea.right - chart.chartArea.left,
											chart.chartArea.bottom - chart.chartArea.top,
										) / 2;

									const labelRadius = chartRadius * 1.06;

									const x = centerX + Math.cos(angle) * labelRadius;
									const y = centerY + Math.sin(angle) * labelRadius;

									ctx.save();
									ctx.translate(x, y);
									if (x < centerX) {
										ctx.textAlign = 'right';
									} else {
										ctx.textAlign = 'left';
									}
									ctx.textBaseline = 'middle';
									ctx.fillStyle = themeMode === 'light' ? '#000000' : '#FFFFFF';

									ctx.restore();
								});

								ctx.restore();
							},
						},
					},
				}) as Chart;
			}
		}

		// Cleanup function to destroy the chart when the component unmounts
		return () => {
			if (chartInstanceRef.current) {
				chartInstanceRef.current.destroy();
			}
		};
	}, [Chart, radialSkillScoreChartRef.current, chartLabels, themeMode]);

	return (
		<div className="w-full flex flex-col gap-8 md:flex-row m-auto justify-center">
			<div className="w-full flex flex-col gap-2 px-6 py-4 text-xs bg-white rounded-2xl text-zinc-900">
				<Box className="flex flex-row gap-4 items-center">
					<Box
						className="flex-shrink-0 text-sm font-semibold font-inter text-[#1C1C1C]"
						style={{ fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on" }}
					>
						Distribution By
					</Box>
					<Box width={'100%'}>
						<Tabs
							value={value}
							onChange={handleChange}
							sx={{
								'& .MuiTabs-indicator': {
									backgroundColor: 'transparent', // Remove the default indicator
								},
								minHeight: 'fit-content',
								display: 'flex', // Arrange tabs horizontally
							}}
						>
							<Tab
								value="score"
								label="Score"
								sx={{
									maxWidth: '100px', // Set width of the tab
									maxHeight: '30px', // Set height of the tab
									minHeight: '40px',
									flex: 1,
									textAlign: 'center',
									border: '1px solid',
									borderRightWidth: '0px',
									borderColor: ThemeContextState.themeMode === 'dark' ? '#555770' : '#EBEBF0',
									borderRadius: '8px',
									borderTopRightRadius: '0px',
									borderBottomRightRadius: '0px',
									padding: '0 2px', // Padding inside each tab
									backgroundColor: ThemeContextState.themeMode === 'dark' ? '#444' : '#F7F7F7',
									color: ThemeContextState.themeMode === 'dark' ? '#E4E4EB' : '#28293D',
									textTransform: 'none',
									fontWeight: '500',
									// fontFamily: 'Inter',
									'&.Mui-selected': {
										backgroundColor: '#9DBFF9',
										color: '#28293D',
										fontWeight: '600',
									},
								}}
							/>
							<Tab
								value="salary"
								label="Salary"
								sx={{
									maxWidth: '100px', // Set width of the tab
									maxHeight: '30px', // Set height of the tab
									minHeight: '40px',
									flex: 1,
									textAlign: 'center',
									border: '1px solid',
									borderLeftWidth: '0px',
									borderColor: ThemeContextState.themeMode === 'dark' ? '#555770' : '#EBEBF0',
									borderRadius: '8px',
									borderTopLeftRadius: '0px',
									borderBottomLeftRadius: '0px',
									padding: '0 2px', // Padding inside each tab
									backgroundColor: ThemeContextState.themeMode === 'dark' ? '#444' : '#F7F7F7',
									color: ThemeContextState.themeMode === 'dark' ? '#E4E4EB' : '#28293D',
									textTransform: 'none',
									fontWeight: '500',
									// fontFamily: 'Inter',
									'&.Mui-selected': {
										backgroundColor: '#9DBFF9',
										color: '#28293D',
										fontWeight: '600',
									},
								}}
							/>
						</Tabs>
					</Box>
				</Box>

				<div className="flex flex-wrap gap-3 justify-center">
					{chartValues?.map((value, index) => (
						<div key={index}>
							<div className="flex flex-row gap-2 items-center">
								(<div className="text-xs text-gray-500">{chartLabels[index]}:</div>
								<div className="text-xs text-gray-500">{value} Students</div>)
							</div>
						</div>
					))}
				</div>
				<div className="md:flex w-[100%] items-start justify-center pt-[20px]">
					<canvas id="radialSkillScoreChart" className="!w-full h-[350px]" ref={radialSkillScoreChartRef}></canvas>
				</div>
			</div>
		</div>
	);
};

export default DistributionChartForSalaryScore;
